/**
 * 24SEVEN Paddle Fit — presentation layer.
 *
 * <paddle-fit-app>    the fitting itself. One implementation, two mounts.
 * <paddle-fit-modal>  the site-wide sheet/panel wrapper.
 *
 * The app never knows which mount it is in, except for one analytics label.
 * All question data lives in paddle-fit-config.js; all maths in paddle-fit-engine.js.
 */

import { Component } from '@theme/component';
import { CartLinesUpdateEvent } from '@shopify/events';
import { PRIORITIES, STAGES, CONFIG_VERSION, fitNote } from '@theme/paddle-fit-config';
import {
  applyRatings,
  createState,
  computePath,
  pruneAnswers,
  isAnswered,
  defaultAnswer,
  stageProgress,
  computeResult,
  PRODUCT_PROFILES,
  PRODUCT_KEYS,
  DISPLAY_ATTRIBUTES,
  attributeDots,
} from '@theme/paddle-fit-engine';

const STORAGE_KEY = `paddle-fit-state:${CONFIG_VERSION}`;

/**
 * Whether the email-marketing box starts ticked. Set to true at Ryan's request.
 * Note: a pre-ticked box is not valid consent under CASL (Canada) or UK/EU/AU
 * rules; flip this to false to require an active opt-in everywhere.
 */
const CONSENT_PRECHECKED = true;
const CALC_MIN_MS = 700;
const CALC_MAX_MS = 1100;

/* ------------------------------------------------------------------ *
 * Small helpers
 * ------------------------------------------------------------------ */

const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value || '').trim());

const prefersReducedMotion = () =>
  window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Analytics. One call fans out to every destination that's present:
 *
 *   1. Shopify.analytics.publish  — Shopify's supported route to web pixels
 *      (your custom pixel forwards these to Meta). Shopify itself withholds
 *      these from pixels until the visitor's cookie consent allows it.
 *   2. Microsoft Clarity          — tags the session recording, so you can
 *      filter recordings by fitting behaviour. Only if Clarity is running.
 *   3. window.dataLayer           — for Google Tag Manager, if one exists.
 *   4. A DOM CustomEvent          — for anything else on the page.
 *
 * Never sent: email address, exact age, names or free text. Age is reduced to
 * a band before it gets here.
 */
const EVENT_PREFIX = '24seven:paddle_fit_';

function analyticsAllowed() {
  try {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (privacy && typeof privacy.analyticsProcessingAllowed === 'function') {
      return !!privacy.analyticsProcessingAllowed();
    }
  } catch (error) {
    /* fall through */
  }
  // No consent API loaded on the page means no consent banner is in force.
  return true;
}

function track(name, payload = {}) {
  const data = {};
  for (const [key, value] of Object.entries(payload)) {
    if (value !== undefined && value !== null && value !== '') data[key] = value;
  }
  const event = `paddle_fit_${name}`;

  try {
    const publish = window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish;
    if (typeof publish === 'function') {
      const result = publish.call(window.Shopify.analytics, `${EVENT_PREFIX}${name}`, data);
      if (result && typeof result.catch === 'function') result.catch(() => {});
    }
  } catch (error) {
    /* never let analytics break the fitting */
  }

  if (analyticsAllowed()) {
    try {
      if (typeof window.clarity === 'function') {
        window.clarity('event', event);
        if (data.best_fit) window.clarity('set', 'paddle_fit_best', data.best_fit);
      }
    } catch (error) {
      /* no-op */
    }
    try {
      if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event, ...data });
    } catch (error) {
      /* no-op */
    }
  }

  try {
    document.dispatchEvent(new CustomEvent(`paddle-fit:${name}`, { detail: { event, ...data }, bubbles: true }));
  } catch (error) {
    /* no-op */
  }
}

const ageBand = (age) => {
  if (typeof age !== 'number') return undefined;
  if (age < 25) return 'under-25';
  if (age < 35) return '25-34';
  if (age < 45) return '35-44';
  if (age < 55) return '45-54';
  if (age < 65) return '55-64';
  return '65-plus';
};

/* ------------------------------------------------------------------ *
 * Icons
 * ------------------------------------------------------------------ */

const ICONS = {
  control: '<circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="3.2"/><path d="M12 1.6v3M12 19.4v3M1.6 12h3M19.4 12h3"/>',
  power: '<path d="M13.4 2 4.6 13.2h6L9.8 22l9-11.4h-6.2z"/>',
  forgiveness: '<path d="M12 2.6 4.2 5.8v6.1c0 4.7 3.2 8.2 7.8 9.5 4.6-1.3 7.8-4.8 7.8-9.5V5.8z"/><path d="M8.7 11.9l2.3 2.4 4.3-4.6"/>',
  quickness: '<path d="M2.6 8h8M2.6 12h5.4M2.6 16h8"/><path d="M13.8 4.4 21 12l-7.2 7.6"/>',
  reach: '<path d="M3 12h18"/><path d="M7.6 7.4 3 12l4.6 4.6"/><path d="M16.4 7.4 21 12l-4.6 4.6"/>',
  pop: '<circle cx="12" cy="12" r="3.4"/><path d="M12 2.4v3.1M12 18.5v3.1M2.4 12h3.1M18.5 12h3.1M5.2 5.2l2.2 2.2M16.6 16.6l2.2 2.2M18.8 5.2l-2.2 2.2M7.4 16.6l-2.2 2.2"/>',
};

const icon = (name, className) =>
  `<svg class="${className}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${
    ICONS[name] || ''
  }</svg>`;

const SHAPES = {
  // Simple paddle silhouettes. viewBox 0 0 46 66.
  widebody: '<path d="M6 4h34a2 2 0 0 1 2 2v30a10 10 0 0 1-10 10H14A10 10 0 0 1 4 36V6a2 2 0 0 1 2-2Z"/><path d="M20 46h6v14a3 3 0 0 1-3 3 3 3 0 0 1-3-3z"/>',
  hybrid: '<path d="M9 4h28a2 2 0 0 1 2 2v32a10 10 0 0 1-10 10H17A10 10 0 0 1 7 38V6a2 2 0 0 1 2-2Z"/><path d="M20 48h6v11a3 3 0 0 1-3 3 3 3 0 0 1-3-3z"/>',
  elongated: '<path d="M13 3h20a2 2 0 0 1 2 2v40a8 8 0 0 1-8 8h-8a8 8 0 0 1-8-8V5a2 2 0 0 1 2-2Z"/><path d="M20 53h6v7a3 3 0 0 1-3 3 3 3 0 0 1-3-3z"/>',
};

const shapeSvg = (name) =>
  `<svg class="pf__shape" viewBox="0 0 46 66" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" aria-hidden="true">${
    SHAPES[name] || SHAPES.hybrid
  }</svg>`;

/* ------------------------------------------------------------------ *
 * <paddle-fit-app>
 * ------------------------------------------------------------------ */

class PaddleFitApp extends Component {
  requiredRefs = ['screen', 'body', 'trackFill', 'nav'];

  connectedCallback() {
    super.connectedCallback();

    this.products = this.#readProducts();
    // The 1–5 ratings set on each product in Shopify drive both the dots and the matching.
    applyRatings(this.products);
    this.availableKeys = PRODUCT_KEYS.filter((key) => this.products[key]);
    this.state = this.#restore() || createState(this.getAttribute('context') || 'page');
    this.state.context = this.getAttribute('context') || 'page';
    this.result = null;
    this.hasStarted = false;

    this.signupForm = this.querySelector('.pf__customer-form');
    this.signupFrame = this.querySelector('[data-pf-signup-frame]');
    if (this.signupForm && this.signupFrame) {
      this.signupForm.target = this.signupFrame.name;
      this.signupFrame.addEventListener('load', this.#onSignupFrameLoad);
    }

    // ONE delegated listener for the whole component. Never rebound.
    this.addEventListener('click', this.#onClick);
    this.addEventListener('input', this.#onInput);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('keydown', this.#onKeydown);

    this.render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('input', this.#onInput);
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('keydown', this.#onKeydown);
  }

  /* ---------------- data ---------------- */

  #readProducts() {
    const node = this.querySelector('[data-paddle-fit-products]');
    if (!node) return {};
    try {
      const parsed = JSON.parse(node.textContent);
      const out = {};
      for (const [key, value] of Object.entries(parsed)) {
        if (value && value.id) out[key] = value;
      }
      return out;
    } catch (error) {
      console.warn('[paddle-fit] product data could not be parsed', error);
      return {};
    }
  }

  get settings() {
    const node = this.querySelector('[data-paddle-fit-settings]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return {};
    }
  }

  /* ---------------- persistence ---------------- */

  #restore() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || !parsed.answers) return null;
      // Never restore straight into the result — recompute from answers instead.
      if (parsed.phase === 'calculating') parsed.phase = 'questions';
      return parsed;
    } catch (error) {
      return null;
    }
  }

  #persist() {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch (error) {
      /* private mode / blocked storage — the fitting still works, it just won't resume */
    }
  }

  #clearStorage() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      /* no-op */
    }
  }

  /* ---------------- derived ---------------- */

  get path() {
    return computePath(this.state.answers);
  }

  get currentQuestion() {
    const path = this.path;
    return path[Math.min(this.state.screenIndex, path.length - 1)];
  }

  /* ---------------- events ---------------- */

  #onClick = (event) => {
    const target = event.target.closest('[data-pf-action]');
    if (!target || !this.contains(target)) return;
    if (target.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      return;
    }

    const action = target.dataset.pfAction;
    const value = target.dataset.pfValue;

    switch (action) {
      case 'select':
        event.preventDefault();
        this.#setAnswer(this.currentQuestion.id, value);
        break;
      case 'toggle-priority':
        event.preventDefault();
        this.#togglePriority(value);
        break;
      case 'scale-stop':
        event.preventDefault();
        this.#setAnswer(this.currentQuestion.id, Number(value));
        break;
      case 'next':
        event.preventDefault();
        this.#goNext();
        break;
      case 'prev':
        event.preventDefault();
        this.#goPrev();
        break;
      case 'submit-email':
        // Deliberately no blanket preventDefault: this is a real submit button
        // for Shopify's form. #handleEmailSubmit cancels it unless the signup
        // should actually be sent.
        this.#handleEmailSubmit(event);
        break;
      case 'skip-email':
        event.preventDefault();
        this.state.emailStatus = 'skipped';
        track('email_skipped', this.#baseAnalytics());
        this.#showResult();
        break;
      case 'variant':
        event.preventDefault();
        this.#selectVariant(target.dataset.pfProduct, value);
        break;
      case 'add-to-cart':
        event.preventDefault();
        this.#addToCart(target);
        break;
      case 'product-link':
        track('product_clicked', { ...this.#baseAnalytics(), product: target.dataset.pfProduct, position: target.dataset.pfPosition });
        break;
      case 'slide':
        event.preventDefault();
        this.#setActiveSlide(Number(value), true);
        break;
      case 'back-to-questions':
        event.preventDefault();
        this.#backToQuestions();
        break;
      case 'restart':
        event.preventDefault();
        this.#restart();
        break;
      default:
        break;
    }
  };

  #onInput = (event) => {
    const range = event.target.closest('[data-pf-range]');
    if (range && this.contains(range)) {
      const question = this.currentQuestion;
      const value = Number(range.value);
      this.state.answers[question.id] = value;
      this.#paintRange(range, question, value);
      this.#updateNav();
      this.#persist();
    }
  };

  #onChange = (event) => {
    const consent = event.target.closest('[data-pf-consent]');
    if (consent) this.#updateNav();
  };

  #onKeydown = () => {
    // Enter in the email field is handled by the browser's own implicit form
    // submission, which clicks the real "See my results" submit button. That
    // keeps the submission a genuine user action, which Shopify's hCaptcha requires.
  };

  /* ---------------- state changes ---------------- */

  #markStarted() {
    if (this.hasStarted) return;
    this.hasStarted = true;
    track('started', { context: this.state.context });
  }

  #setAnswer(questionId, value) {
    this.#markStarted();
    this.state.answers[questionId] = value;
    // Re-derive the path and drop anything that is no longer reachable.
    this.state.answers = pruneAnswers(this.state.answers);
    this.#persist();
    this.render({ animate: false });
  }

  #togglePriority(id) {
    this.#markStarted();
    const question = this.currentQuestion;
    const max = question.maxSelections || 3;
    const current = Array.isArray(this.state.answers[question.id]) ? [...this.state.answers[question.id]] : [];
    const index = current.indexOf(id);

    if (index >= 0) current.splice(index, 1);
    else if (current.length < max) current.push(id);
    else return;

    this.state.answers[question.id] = current;
    this.#persist();
    this.render({ animate: false });

    if (current.length === max) {
      track('priorities_selected', { ...this.#baseAnalytics(), priorities: current.join('|') });
    }
  }

  #goNext() {
    const path = this.path;
    const question = this.currentQuestion;

    if (question && question.type !== 'interstitial' && !isAnswered(question, this.state.answers)) return;

    const previousStage = question?.stage;

    if (this.state.screenIndex >= path.length - 1) {
      if (this.state.phase === 'questions') {
        track('stage_completed', { context: this.state.context, stage: previousStage });
        this.state.phase = 'email';
        this.#persist();
        this.render();
      }
      return;
    }

    this.state.screenIndex += 1;
    const nextStage = this.path[this.state.screenIndex]?.stage;
    if (nextStage && nextStage !== previousStage) {
      track('stage_completed', { context: this.state.context, stage: previousStage });
    }
    this.#persist();
    this.render();
  }

  #goPrev() {
    if (this.state.phase === 'email') {
      this.state.phase = 'questions';
      this.#persist();
      this.render();
      return;
    }
    if (this.state.phase === 'result') {
      this.state.phase = 'email';
      this.#persist();
      this.render();
      return;
    }
    if (this.state.screenIndex > 0) {
      this.state.screenIndex -= 1;
      this.#persist();
      this.render();
    }
  }

  /**
   * Return to the last question with every answer intact, so a customer can
   * adjust one thing and re-run the fit. The cached result is discarded so the
   * recommendation is genuinely recomputed rather than replayed.
   */
  #backToQuestions() {
    this.result = null;
    this.state.phase = 'questions';
    this.state.emailStatus = 'pending';
    this.state.screenIndex = Math.max(0, this.path.length - 1);
    this.#persist();
    this.render();
  }

  #restart() {
    this.#clearStorage();
    this.state = createState(this.state.context);
    this.result = null;
    this.hasStarted = false;
    this.render();
  }

  #baseAnalytics() {
    const a = this.state.answers;
    const priorities = Array.isArray(a.priorities) ? a.priorities : [];
    return {
      context: this.state.context,
      priorities: priorities.join('|') || undefined,
      sex: a.sex,
      experience: a.experience,
      age_band: ageBand(a.age),
    };
  }

  /** Fire step_viewed once per screen shown, so drop-off can be read per question. */
  #trackStep() {
    let id;
    let index;
    if (this.state.phase === 'questions') {
      const q = this.currentQuestion;
      if (!q) return;
      id = q.id;
      index = this.state.screenIndex + 1;
    } else if (this.state.phase === 'email') {
      id = 'email';
    } else {
      return;
    }
    const key = `${this.state.phase}:${id}`;
    if (this.lastTrackedStep === key) return;
    this.lastTrackedStep = key;
    track('step_viewed', {
      context: this.state.context,
      step: id,
      step_number: index,
      stage: this.state.phase === 'questions' ? this.currentQuestion.stage : 'fit',
    });
  }

  /* ---------------- render ---------------- */

  render({ animate = true } = {}) {
    if (this.state.phase === 'result' && !this.result) {
      this.result = computeResult(this.state.answers, this.availableKeys);
    }

    // Sliders and scales render with a visible starting value, so that value has
    // to count as the answer. Gating Next behind "drag me first" looks broken:
    // the control plainly shows 42, yet the button is greyed out with no reason
    // given. Both reference fittings treat a shown value as a set value.
    const pending = this.state.phase === 'questions' ? this.currentQuestion : null;
    if (pending && (pending.type === 'slider' || pending.type === 'scale') && this.state.answers[pending.id] === undefined) {
      this.state.answers[pending.id] = defaultAnswer(pending);
    }

    this.#renderChrome();
    const html = this.#renderScreenHtml();

    const screen = this.refs.screen;
    screen.innerHTML = html;
    if (animate && !prefersReducedMotion()) {
      screen.setAttribute('data-enter', '');
      // force restart of the animation
      void screen.offsetWidth;
    } else {
      screen.removeAttribute('data-enter');
    }

    this.#updateNav();
    this.#trackStep();
    if (this.state.phase === 'result') this.#bindCarousel();

    // Reset scroll to the top of the new screen, not the page.
    if (this.refs.body) this.refs.body.scrollTop = 0;

    const range = screen.querySelector('[data-pf-range]');
    if (range) this.#paintRange(range, this.currentQuestion, Number(range.value));

    this.#focusScreen();
  }

  #focusScreen() {
    const heading = this.refs.screen.querySelector('[data-pf-heading]');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      // Only steal focus if focus is already inside the fitting, so we never
      // yank it away from someone typing elsewhere on the page.
      if (this.contains(document.activeElement) && document.activeElement !== document.body) {
        heading.focus({ preventScroll: true });
      }
    }
  }

  #renderChrome() {
    const path = this.path;
    let fraction;
    let currentStage;

    if (this.state.phase === 'result') {
      fraction = 1;
      currentStage = '__done__';
    } else if (this.state.phase === 'email' || this.state.phase === 'calculating') {
      fraction = 0.97;
      currentStage = STAGES[STAGES.length - 1].id;
    } else {
      const progress = stageProgress(path, this.state.screenIndex);
      fraction = progress.fraction;
      currentStage = progress.currentStage;
    }

    this.refs.trackFill.style.width = `${Math.max(4, Math.round(fraction * 100))}%`;

    const currentIndex = STAGES.findIndex((s) => s.id === currentStage);
    this.querySelectorAll('[data-pf-stage]').forEach((node, index) => {
      const state = currentStage === '__done__' || index < currentIndex ? 'done' : index === currentIndex ? 'current' : 'todo';
      node.setAttribute('data-state', state);
      if (state === 'current') node.setAttribute('aria-current', 'step');
      else node.removeAttribute('aria-current');
    });
  }

  #renderScreenHtml() {
    if (this.state.phase === 'email') return this.#renderEmail();
    if (this.state.phase === 'calculating') return this.#renderCalculating();
    if (this.state.phase === 'result') return this.#renderResult();

    const question = this.currentQuestion;
    if (!question) return '';

    switch (question.type) {
      case 'interstitial':
        return this.#renderInterstitial(question);
      case 'priorities':
        return this.#renderPriorities(question);
      case 'slider':
        return this.#renderSlider(question);
      case 'scale':
        return this.#renderScale(question);
      case 'shape':
        return this.#renderOptions(question, true);
      default:
        return this.#renderOptions(question, false);
    }
  }

  #head(question) {
    return `
      <h2 class="pf__title" data-pf-heading>${escapeHtml(question.title)}</h2>
      ${question.why ? `<p class="pf__why">${escapeHtml(question.why)}</p>` : ''}
    `;
  }

  #renderOptions(question, isShape) {
    const selected = this.state.answers[question.id];
    const options = question.options
      .map((option) => {
        const checked = selected === option.value;
        return `
          <button type="button" class="pf__option" role="radio" aria-checked="${checked}"
                  data-pf-action="select" data-pf-value="${escapeHtml(option.value)}">
            ${isShape ? shapeSvg(option.shape) : '<span class="pf__marker" aria-hidden="true"></span>'}
            <span>
              <span class="pf__option-label">${escapeHtml(option.label)}</span>
              ${option.desc ? `<span class="pf__option-desc">${escapeHtml(option.desc)}</span>` : ''}
            </span>
          </button>`;
      })
      .join('');

    return `
      ${this.#head(question)}
      <div class="pf__options ${isShape ? 'pf__options--shape' : ''}" role="radiogroup" aria-label="${escapeHtml(question.title)}">
        ${options}
      </div>
    `;
  }

  #renderPriorities(question) {
    const selected = Array.isArray(this.state.answers[question.id]) ? this.state.answers[question.id] : [];
    const max = question.maxSelections || 3;
    const atMax = selected.length >= max;

    const cards = PRIORITIES.map((priority) => {
      const index = selected.indexOf(priority.id);
      const pressed = index >= 0;
      const disabled = !pressed && atMax;
      return `
        <button type="button" class="pf__priority" aria-pressed="${pressed}" aria-disabled="${disabled}"
                data-pf-action="toggle-priority" data-pf-value="${escapeHtml(priority.id)}">
          ${pressed ? `<span class="pf__priority-rank">${index + 1}</span>` : ''}
          ${icon(priority.icon, 'pf__priority-icon')}
          <span class="pf__priority-label">${escapeHtml(priority.label)}</span>
          <span class="pf__priority-blurb">${escapeHtml(priority.blurb)}</span>
        </button>`;
    }).join('');

    return `
      ${this.#head(question)}
      <div class="pf__instruction">
        <span>${escapeHtml(question.instruction || 'Choose your top 3')}</span>
        <span class="pf__counter" data-complete="${atMax}" aria-live="polite">
          ${selected.length} of ${max} selected
        </span>
      </div>
      <div class="pf__priorities">${cards}</div>
    `;
  }

  #renderSlider(question) {
    const value = typeof this.state.answers[question.id] === 'number' ? this.state.answers[question.id] : defaultAnswer(question);
    const display = question.format ? question.format(value) : String(value);
    return `
      ${this.#head(question)}
      <div class="pf__slider-wrap">
        <div class="pf__readout">
          <span data-pf-readout>${escapeHtml(display)}</span>
          <span class="pf__readout-unit">Years old</span>
        </div>
        <input type="range" class="pf__range" data-pf-range
               min="${question.min}" max="${question.max}" step="${question.step || 1}" value="${value}"
               aria-label="${escapeHtml(question.title)}"
               aria-valuetext="${escapeHtml(question.valueText ? question.valueText(value) : String(value))}">
        <div class="pf__scale-labels">
          <span>${question.min}</span>
          <span>${question.max}+</span>
        </div>
      </div>
    `;
  }

  #renderScale(question) {
    const value = typeof this.state.answers[question.id] === 'number' ? this.state.answers[question.id] : defaultAnswer(question);
    const stops = question.stops;
    const current = stops.find((s) => s.value === value) || stops[Math.floor(stops.length / 2)];

    const buttons = stops
      .map(
        (stop) => `
        <button type="button" class="pf__scale-stop" aria-pressed="${stop.value === value}"
                data-pf-action="scale-stop" data-pf-value="${stop.value}"
                aria-label="${escapeHtml(stop.label)}"></button>`
      )
      .join('');

    return `
      ${this.#head(question)}
      <div class="pf__slider-wrap">
        <p class="pf__scale-current" data-pf-readout aria-live="polite">${escapeHtml(current.label)}</p>
        <input type="range" class="pf__range" data-pf-range
               min="0" max="${stops.length - 1}" step="1" value="${value}"
               aria-label="${escapeHtml(question.title)}"
               aria-valuetext="${escapeHtml(current.label)}">
        <div class="pf__scale-stops">${buttons}</div>
        <div class="pf__scale-labels">
          <span>${escapeHtml(stops[0].label)}</span>
          <span>${escapeHtml(stops[stops.length - 1].label)}</span>
        </div>
      </div>
    `;
  }

  #paintRange(range, question, value) {
    const min = Number(range.min);
    const max = Number(range.max);
    const pct = max > min ? ((value - min) / (max - min)) * 100 : 50;
    range.style.setProperty('--pf-pct', `${pct}%`);

    const readout = this.refs.screen.querySelector('[data-pf-readout]');
    if (!readout || !question) return;

    if (question.type === 'scale') {
      const stop = question.stops.find((s) => s.value === value);
      if (stop) {
        readout.textContent = stop.label;
        range.setAttribute('aria-valuetext', stop.label);
        this.refs.screen.querySelectorAll('[data-pf-action="scale-stop"]').forEach((node) => {
          node.setAttribute('aria-pressed', String(Number(node.dataset.pfValue) === value));
        });
      }
    } else {
      readout.textContent = question.format ? question.format(value) : String(value);
      range.setAttribute('aria-valuetext', question.valueText ? question.valueText(value) : String(value));
    }
  }

  #renderInterstitial(question) {
    const data = question.build(this.state);
    const items = (data.items || [])
      .map((item, index) => `<li><span class="pf__recap-num">0${index + 1}</span>${escapeHtml(item)}</li>`)
      .join('');

    return `
      <div class="pf__interstitial">
        <p class="pf__eyebrow"><span class="pf__tick" aria-hidden="true">✓</span>${escapeHtml(question.kicker)}</p>
        <h2 class="pf__title" data-pf-heading>${escapeHtml(data.title)}</h2>
        <ul class="pf__recap">${items}</ul>
        <p class="pf__interstitial-footer">${escapeHtml(data.footer)}</p>
      </div>
    `;
  }

  #renderEmail() {
    const settings = this.settings;
    return `
      <p class="pf__eyebrow">Almost there</p>
      <h2 class="pf__title" data-pf-heading>${escapeHtml(settings.emailHeading || 'Your fit is ready.')}</h2>
      <p class="pf__why">${escapeHtml(
        settings.emailBody || "Add your email if you'd like us to send your result and keep you posted. It's optional — your recommendation shows either way."
      )}</p>
      <div class="pf__field">
        <label class="pf-visually-hidden" for="pf-email">Email address</label>
        <input class="pf__input" id="pf-email" type="email" inputmode="email" autocomplete="email"
               autocapitalize="off" autocorrect="off" spellcheck="false" enterkeyhint="go"
               form="${escapeHtml(this.signupForm?.id || '')}" formnovalidate
               placeholder="your@email.com" data-pf-email>
        <p class="pf__error" data-pf-email-error hidden></p>
      </div>
      <label class="pf__consent">
        <input type="checkbox" data-pf-consent ${CONSENT_PRECHECKED ? 'checked' : ''}>
        <span>${escapeHtml(
          settings.consentLabel || 'Send me 24SEVEN paddle guides, product news and special offers. Unsubscribe any time.'
        )}</span>
      </label>
      <p class="pf__note">We only save your email while the box is ticked. Leave the email blank if you'd prefer to skip — your recommendation still shows on the next screen.</p>
      <button type="button" class="pf__skip" data-pf-action="skip-email">Skip and see my result</button>
    `;
  }

  #renderCalculating() {
    return `
      <div class="pf__calc">
        <div class="pf__calc-ring" aria-hidden="true"></div>
        <p role="status" data-pf-heading>Building your fit</p>
      </div>
    `;
  }

  /* ---------------- result ---------------- */

  #showResult() {
    this.state.phase = 'calculating';
    this.result = computeResult(this.state.answers, this.availableKeys);
    this.render();

    const delay = prefersReducedMotion() ? 0 : CALC_MIN_MS + Math.random() * (CALC_MAX_MS - CALC_MIN_MS);
    window.setTimeout(() => {
      if (!this.isConnected) return;
      this.state.phase = 'result';
      this.#persist();
      this.render();
      track('completed', {
        ...this.#baseAnalytics(),
        best_fit: this.result.bestKey,
        alternative_fit: this.result.alternativeKey,
        best_fit_product_id: this.products[this.result.bestKey]?.id,
      });
      track('result_viewed', { ...this.#baseAnalytics(), best_fit: this.result.bestKey });
    }, delay);
  }

  #variantFor(key) {
    const product = this.products[key];
    if (!product) return null;
    const chosenId = this.state.selectedVariant[key];
    return product.variants.find((v) => String(v.id) === String(chosenId)) || product.variants.find((v) => v.available) || product.variants[0];
  }

  #selectVariant(key, variantId) {
    this.state.selectedVariant[key] = variantId;
    this.#persist();

    const variant = this.#variantFor(key);
    const card = this.refs.screen.querySelector(`[data-pf-card="${key}"]`);
    if (!card || !variant) return;

    const img = card.querySelector('[data-pf-image]');
    if (img && variant.image) {
      img.src = variant.image;
      img.alt = `${this.products[key].title} — ${variant.title}`;
    }
    card.querySelectorAll('[data-pf-action="variant"]').forEach((node) => {
      node.setAttribute('aria-pressed', String(String(node.dataset.pfValue) === String(variantId)));
    });
    const price = card.querySelector('[data-pf-price]');
    if (price && variant.price) price.innerHTML = variant.price;

    const atc = card.querySelector('[data-pf-action="add-to-cart"]');
    if (atc) {
      atc.dataset.pfVariant = variant.id;
      atc.setAttribute('aria-disabled', String(!variant.available));
      atc.textContent = variant.available ? 'Add to cart' : 'Sold out';
    }
  }

  #renderCard(key, isBest) {
    const product = this.products[key];
    const profile = PRODUCT_PROFILES[key];
    if (!product || !profile) return '';

    const variant = this.#variantFor(key);
    const showColorways = product.variants.length > 1;

    const attrs = DISPLAY_ATTRIBUTES.map((attr) => {
      const dots = attributeDots(key, attr.key, this.availableKeys);
      const rendered = Array.from({ length: 5 }, (_, i) => `<span class="pf__dot" data-on="${i < dots}"></span>`).join('');
      return `<div class="pf__attr"><span>${escapeHtml(attr.label)}</span><span class="pf__dots" role="img" aria-label="${escapeHtml(
        attr.label
      )}: ${dots} out of 5">${rendered}</span></div>`;
    }).join('');

    const swatches = showColorways
      ? `<div class="pf__colorways">
           <span class="pf__colorways-label">Colourway</span>
           <div class="pf__swatches">
             ${product.variants
               .map(
                 (v) => `<button type="button" class="pf__swatch"
                           aria-pressed="${String(v.id) === String(variant.id)}"
                           data-available="${v.available}"
                           data-pf-action="variant" data-pf-product="${escapeHtml(key)}" data-pf-value="${escapeHtml(v.id)}">
                           ${escapeHtml(v.title)}
                         </button>`
               )
               .join('')}
           </div>
         </div>`
      : '';

    return `
      <article class="pf__card ${isBest ? 'pf__card--best' : 'pf__card--alt'}" data-pf-card="${escapeHtml(key)}">
        <span class="pf__badge">${isBest ? 'Best fit' : 'Alternative fit'}</span>
        <div class="pf__card-media">
          ${
            variant && (variant.image || product.image)
              ? `<img data-pf-image src="${escapeHtml(variant.image || product.image)}" alt="${escapeHtml(
                  product.title
                )}" width="600" height="600" loading="lazy">`
              : ''
          }
        </div>
        <h3 class="pf__card-name">${escapeHtml(product.title)}</h3>
        <p class="pf__card-positioning">${escapeHtml(profile.positioning)}</p>
        <p class="pf__price" data-pf-price>${variant ? variant.price : product.price}</p>
        <div class="pf__attrs">${attrs}</div>
        ${swatches}
        <div class="pf__card-actions">
          <button type="button" class="${isBest ? 'button' : 'button-secondary'}"
                  data-pf-action="add-to-cart" data-pf-product="${escapeHtml(key)}"
                  data-pf-variant="${variant ? escapeHtml(variant.id) : ''}"
                  aria-disabled="${!(variant && variant.available)}">
            ${variant && variant.available ? 'Add to cart' : 'Sold out'}
          </button>
          <a class="button-secondary" href="${escapeHtml(product.url)}"
             data-pf-action="product-link" data-pf-product="${escapeHtml(key)}" data-pf-position="${isBest ? 'best' : 'alternative'}">View product</a>
          <p class="pf__card-error" data-pf-cart-error role="alert" hidden></p>
        </div>
      </article>
    `;
  }

  #renderResult() {
    const { bestKey, alternativeKey, reasons, alternativeReasons = [], tradeoff = '' } = this.result;
    const bestName = PRODUCT_PROFILES[bestKey]?.name || '';
    const altName = alternativeKey ? PRODUCT_PROFILES[alternativeKey]?.name || '' : '';
    const note = fitNote(this.state.answers, bestName);

    const reasonList = (list) =>
      list.map((r) => `<div class="pf__reason"><h4>${escapeHtml(r.heading)}</h4><p>${escapeHtml(r.body)}</p></div>`).join('');

    const bestWhy = `
      <section class="pf__why-picked" aria-labelledby="pf-why-best">
        <h3 id="pf-why-best">Why ${escapeHtml(bestName)} is your best fit</h3>
        ${note ? `<p class="pf__fit-note">${escapeHtml(note)}</p>` : ''}
        <div class="pf__reasons">${reasonList(reasons)}</div>
      </section>`;

    const altWhy = alternativeKey
      ? `
      <section class="pf__why-picked" aria-labelledby="pf-why-alt">
        <h3 id="pf-why-alt">Why ${escapeHtml(altName)} could also suit you</h3>
        <div class="pf__reasons">
          ${reasonList(alternativeReasons)}
          ${tradeoff ? `<div class="pf__reason pf__reason--tradeoff"><h4>The trade-off</h4><p>${escapeHtml(tradeoff)}</p></div>` : ''}
        </div>
      </section>`
      : '';

    // Two slides, each a card with its own explanation beneath it. Below 990px
    // this is a swipeable carousel (best fit first, alternative peeking in from
    // the right); on desktop the two slides sit side by side.
    return `
      <div class="pf__result-head">
        <p class="pf__eyebrow">Your fitting is complete</p>
        <h2 class="pf__title" data-pf-heading>Your paddle</h2>
      </div>
      <div class="pf__email-notice" data-pf-email-notice ${this.#emailNoticeHtml() ? '' : 'hidden'}>${this.#emailNoticeHtml()}</div>
      ${
        alternativeKey
          ? `<div class="pf__switch" role="tablist" aria-label="Your recommendations">
              <button type="button" role="tab" class="pf__switch-tab" id="pf-tab-0" aria-controls="pf-slide-0" aria-selected="true" data-pf-action="slide" data-pf-value="0">Best fit</button>
              <button type="button" role="tab" class="pf__switch-tab" id="pf-tab-1" aria-controls="pf-slide-1" aria-selected="false" tabindex="-1" data-pf-action="slide" data-pf-value="1">Alternative</button>
            </div>`
          : ''
      }
      <div class="pf__results" data-pf-carousel aria-roledescription="carousel" aria-label="Your paddle recommendations">
        <div class="pf__slide" id="pf-slide-0" role="group" aria-roledescription="slide" aria-label="1 of ${alternativeKey ? 2 : 1}: best fit, ${escapeHtml(bestName)}" data-pf-slide="0">
          ${this.#renderCard(bestKey, true)}
          ${bestWhy}
        </div>
        ${
          alternativeKey
            ? `<div class="pf__slide" id="pf-slide-1" role="group" aria-roledescription="slide" aria-label="2 of 2: alternative fit, ${escapeHtml(altName)}" data-pf-slide="1">
                ${this.#renderCard(alternativeKey, false)}
                ${altWhy}
              </div>`
            : ''
        }
      </div>
      <div class="pf__restart">
        <button type="button" data-pf-action="back-to-questions">Change my answers</button>
        <span aria-hidden="true">·</span>
        <button type="button" data-pf-action="restart">Start over</button>
      </div>
    `;
  }

  /* ---------------- result carousel ---------------- */

  #bindCarousel() {
    const rail = this.refs.screen.querySelector('[data-pf-carousel]');
    if (!rail) return;
    this.altViewed = false;
    let frame = 0;
    // Bound to the carousel element, which is replaced on every render, so the
    // listener is released with it — no accumulation across renders.
    rail.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => this.#setActiveSlide(this.#nearestSlide(rail), false));
      },
      { passive: true }
    );
  }

  #nearestSlide(rail) {
    const slides = [...rail.querySelectorAll('[data-pf-slide]')];
    const left = rail.scrollLeft;
    let best = 0;
    let bestDistance = Infinity;
    const maxScroll = rail.scrollWidth - rail.clientWidth;
    slides.forEach((slide, index) => {
      const target = Math.min(slide.offsetLeft - slides[0].offsetLeft, maxScroll);
      const distance = Math.abs(target - left);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });
    return best;
  }

  #setActiveSlide(index, scroll) {
    const rail = this.refs.screen.querySelector('[data-pf-carousel]');
    if (!rail) return;
    const slides = [...rail.querySelectorAll('[data-pf-slide]')];
    const slide = slides[index];
    if (!slide) return;

    if (scroll) {
      const left = Math.min(slide.offsetLeft - slides[0].offsetLeft, rail.scrollWidth - rail.clientWidth);
      rail.scrollTo({ left, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }

    this.refs.screen.querySelectorAll('[data-pf-action="slide"]').forEach((tab) => {
      const active = Number(tab.dataset.pfValue) === index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });

    if (index === 1 && !this.altViewed) {
      this.altViewed = true;
      track('alternative_viewed', {
        ...this.#baseAnalytics(),
        best_fit: this.result?.bestKey,
        alternative_fit: this.result?.alternativeKey,
      });
    }
  }

  /* ---------------- email ---------------- */

  /**
   * Runs on the click of the real "See my results" submit button (or the
   * browser's implicit Enter-key submission, which clicks it for us).
   *
   * If the signup should be sent, we fill Shopify's form and let the click's
   * default action submit it natively — that is what lets Shopify's hCaptcha
   * protection attach its token. Otherwise we cancel the submission.
   * Either way, the result is shown immediately; the signup never gates it.
   */
  #handleEmailSubmit(event) {
    const input = this.refs.screen.querySelector('[data-pf-email]');
    const consent = this.refs.screen.querySelector('[data-pf-consent]');
    const errorNode = this.refs.screen.querySelector('[data-pf-email-error]');
    const value = (input?.value || '').trim();

    if (errorNode) errorNode.hidden = true;
    input?.removeAttribute('aria-invalid');

    const skip = () => {
      event.preventDefault();
      this.state.emailStatus = 'skipped';
      this.result = computeResult(this.state.answers, this.availableKeys);
      this.#showResult();
    };

    // Blank email is a valid choice — it means "just show me the result".
    if (!value) return skip();

    if (!isValidEmail(value)) {
      event.preventDefault();
      if (errorNode) {
        errorNode.textContent = 'That email address doesn’t look right. Check it, or leave it blank to skip.';
        errorNode.hidden = false;
      }
      input?.setAttribute('aria-invalid', 'true');
      input?.focus();
      return;
    }

    // Shopify's customer form always grants marketing consent. With the box
    // unticked we send nothing rather than subscribe someone who declined.
    if (!consent || !consent.checked) return skip();

    if (!this.signupForm) return skip();

    // Compute the fit BEFORE building tags — the fit-<paddle> tag depends on it.
    this.result = computeResult(this.state.answers, this.availableKeys);
    const emailField = this.signupForm.querySelector('input[name="contact[email]"]');
    const tagsField = this.signupForm.querySelector('input[name="contact[tags]"]');
    if (emailField) emailField.value = value;
    if (tagsField) tagsField.value = this.#customerTags().join(',');

    this.state.emailStatus = 'sending';
    this.#persist();

    // Let the native submission start before the screen re-renders.
    window.setTimeout(() => this.#showResult(), 0);
  }

  /**
   * Shopify's reply to the signup lands in the hidden frame. Read what we can:
   * a confirmed success, a visible spam challenge, or — if the browser won't let
   * us look inside — the fact that Shopify answered at all.
   */
  #onSignupFrameLoad = () => {
    if (this.state.emailStatus !== 'sending') return;

    let href = '';
    try {
      href = this.signupFrame.contentWindow.location.href;
    } catch (error) {
      href = 'unreadable';
    }
    if (!href || href === 'about:blank') return;

    if (/\/challenge(\b|\/|\?|$)/.test(href)) {
      this.state.emailStatus = 'challenge';
    } else {
      this.state.emailStatus = 'submitted';
      track('email_submitted', { ...this.#baseAnalytics(), best_fit: this.result?.bestKey, consent: true });
    }
    this.#persist();
    this.#refreshEmailNotice();
  };

  #emailNoticeHtml() {
    switch (this.state.emailStatus) {
      case 'submitted':
        return `<p class="pf__notice pf__notice--ok">You're on the list — paddle guides and news will come to your inbox.</p>`;
      case 'challenge':
        return `<div class="pf__notice">
            <p>One quick step to finish signing up: Shopify needs to confirm you're not a bot.</p>
            <button type="submit" form="${escapeHtml(this.signupForm?.id || '')}" formtarget="_blank" class="button-secondary">Confirm my signup</button>
          </div>`;
      default:
        // 'sending', 'skipped', 'pending': nothing to say. We never claim a
        // failure we can't confirm — the old "couldn't save" message came from
        // misreading a captcha rejection, not from anything the customer did.
        return '';
    }
  }

  #refreshEmailNotice() {
    const slot = this.refs.screen?.querySelector('[data-pf-email-notice]');
    if (!slot) return;
    const html = this.#emailNoticeHtml();
    slot.innerHTML = html;
    slot.hidden = !html;
  }

  #customerTags() {
    const tags = ['paddle-fit'];
    if (this.result?.bestKey) tags.push(`fit-${this.result.bestKey}`);
    const priorities = Array.isArray(this.state.answers.priorities) ? this.state.answers.priorities : [];
    priorities.forEach((p) => tags.push(`priority-${p}`));
    if (this.state.answers.sex === 'male' || this.state.answers.sex === 'female') tags.push(`sex-${this.state.answers.sex}`);
    return tags;
  }

  /* ---------------- cart ---------------- */

  async #addToCart(button) {
    const variantId = button.dataset.pfVariant;
    const productKey = button.dataset.pfProduct;
    if (!variantId || button.dataset.pfBusy === 'true') return;

    const card = button.closest('[data-pf-card]');
    const errorNode = card?.querySelector('[data-pf-cart-error]');
    if (errorNode) errorNode.hidden = true;

    button.dataset.pfBusy = 'true';
    const originalLabel = button.textContent.trim();
    button.textContent = 'Adding…';

    // `Theme` is declared with `const` in a classic script, so it is a global
    // lexical binding — visible here by name, but NOT as window.Theme.
    const routes = typeof Theme !== 'undefined' && Theme.routes ? Theme.routes : {};
    const addUrl = routes.cart_add_url || '/cart/add.js';
    const cartJsonUrl = `${routes.cart_url || '/cart'}.js`;

    // Ask Shopify to render the cart drawer's section(s) in the same response,
    // exactly as Horizon's product form does.
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((node) => node.dataset.sectionId)
      .filter(Boolean);

    const formData = new FormData();
    formData.set('id', variantId);
    formData.set('quantity', '1');
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    // Close our sheet first so the cart drawer never opens underneath it.
    const modal = this.closest('paddle-fit-modal');
    if (modal && modal.isOpen) modal.close();

    // Horizon's cart UI (drawer contents, count bubble, auto-open) listens for
    // this event and waits on `event.promise`. Without a promise every listener
    // silently ignores the event — which is why the first version opened an
    // un-refreshed drawer. Dispatched BEFORE the request, like the theme does.
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'paddle-fit',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    const toCart = (ajaxCart) =>
      ajaxCart && typeof CartLinesUpdateEvent.createCartFromAjaxResponse === 'function'
        ? CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart)
        : undefined;

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.status) {
        // Shopify refused (sold out, quantity limit…). Resolve rather than reject,
        // matching the theme, so its listeners settle cleanly.
        deferred.resolve({
          cart: undefined,
          detail: { items: [], source: 'paddle-fit', sourceId: 'paddle-fit', itemCount: 0, sections: undefined, didError: true },
        });
        throw new Error(data.description || data.message || `Add to cart failed (${response.status})`);
      }

      const ajaxCart = await fetch(cartJsonUrl, { headers: { Accept: 'application/json' } }).then((r) => r.json());

      deferred.resolve({
        cart: toCart(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'paddle-fit',
          sourceId: 'paddle-fit',
          itemCount: 1,
          productId: this.products[productKey]?.id,
          sections: data.sections,
          didError: false,
        },
      });

      track('add_to_cart', {
        ...this.#baseAnalytics(),
        product: productKey,
        position: productKey === this.result?.bestKey ? 'best' : 'alternative',
        variant: this.products[productKey]?.variants.find((v) => String(v.id) === String(variantId))?.title,
      });

      // The theme's drawer opens itself off the event above. This is only a
      // fallback for stores where the drawer lacks auto-open.
      window.setTimeout(() => {
        const drawer = document.getElementById('cart-drawer');
        if (drawer && typeof drawer.open === 'function' && !drawer.isOpen) drawer.open();
      }, 250);

      button.textContent = 'Added';
      window.setTimeout(() => {
        if (button.isConnected) button.textContent = originalLabel;
      }, 1800);
    } catch (error) {
      deferred.reject(error); // no-op if already resolved above
      console.warn('[paddle-fit] add to cart failed', error);
      button.textContent = originalLabel;
      if (errorNode) {
        errorNode.textContent = error?.message && !/^Add to cart failed/.test(error.message)
          ? error.message
          : "We couldn't add that to your cart. Try again, or view the product.";
        errorNode.hidden = false;
      }
    } finally {
      button.dataset.pfBusy = 'false';
    }
  }

  /* ---------------- nav ---------------- */

  #updateNav() {
    const nav = this.refs.nav;
    if (!nav) return;

    const prev = nav.querySelector('[data-pf-action="prev"]');
    const next = nav.querySelector('[data-pf-action="next"]');
    const hint = nav.querySelector('[data-pf-hint]');

    if (this.state.phase === 'result') {
      nav.hidden = true;
      return;
    }
    nav.hidden = false;

    const atStart = this.state.phase === 'questions' && this.state.screenIndex === 0;
    if (prev) prev.hidden = atStart;

    if (this.state.phase === 'calculating') {
      if (next) next.setAttribute('aria-disabled', 'true');
      return;
    }

    if (this.state.phase === 'email') {
      if (next) {
        next.hidden = true;
        next.setAttribute('aria-disabled', 'false');
      }
      const submit = nav.querySelector('[data-pf-action="submit-email"]');
      if (submit) submit.hidden = false;
      if (hint) hint.textContent = '';
      return;
    }

    const submit = nav.querySelector('[data-pf-action="submit-email"]');
    if (submit) submit.hidden = true;
    if (next) next.hidden = false;

    const question = this.currentQuestion;
    const ready = !question || question.type === 'interstitial' || isAnswered(question, this.state.answers);
    if (next) {
      next.setAttribute('aria-disabled', String(!ready));
      const path = this.path;
      const isLast = this.state.screenIndex >= path.length - 1;
      next.textContent = question?.type === 'interstitial' ? 'Continue' : isLast ? 'See my fit' : 'Next';
    }

    if (hint) {
      if (question?.type === 'priorities' && !ready) {
        const selected = Array.isArray(this.state.answers.priorities) ? this.state.answers.priorities.length : 0;
        hint.textContent = `Pick ${3 - selected} more`;
      } else {
        hint.textContent = '';
      }
    }
  }
}

if (!customElements.get('paddle-fit-app')) customElements.define('paddle-fit-app', PaddleFitApp);

/* ------------------------------------------------------------------ *
 * <paddle-fit-modal>
 * ------------------------------------------------------------------ */

class PaddleFitModal extends HTMLElement {
  #dialog = null;
  #opener = null;
  #scrollLocked = false;
  #previousOverflow = '';
  #sheet = null;
  #drag = null;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;

    this.#dialog.addEventListener('cancel', this.#onCancel);
    this.#dialog.addEventListener('click', this.#onBackdropClick);
    this.addEventListener('click', this.#onCloseClick);

    this.#sheet = this.#dialog.querySelector('.pf-dialog__inner');
    if (this.#sheet) {
      this.#sheet.addEventListener('touchstart', this.#onTouchStart, { passive: true });
      this.#sheet.addEventListener('touchmove', this.#onTouchMove, { passive: false });
      this.#sheet.addEventListener('touchend', this.#onTouchEnd);
      this.#sheet.addEventListener('touchcancel', this.#onTouchEnd);
    }
  }

  disconnectedCallback() {
    this.#unlock();
  }

  get isOpen() {
    return !!this.#dialog?.open;
  }

  open(opener) {
    if (!this.#dialog || this.isOpen) return;
    this.#opener = opener || null;
    this.#dialog.removeAttribute('data-closing');
    this.#dialog.showModal();
    this.#lock();
    track('opened', { context: 'modal' });
  }

  close({ immediate = false } = {}) {
    if (!this.#dialog || !this.isOpen) return;

    const finish = () => {
      this.#dialog.removeAttribute('data-closing');
      this.#dialog.removeAttribute('data-swiped');
      this.#dialog.style.removeProperty('--pf-sheet-drag');
      if (this.#sheet) {
        this.#sheet.style.transform = '';
        this.#sheet.style.transition = '';
      }
      this.#drag = null;
      this.#dialog.close();
      this.#unlock();
      if (this.#opener && document.contains(this.#opener)) {
        this.#opener.focus({ preventScroll: true });
      }
      this.#opener = null;
    };

    if (immediate || prefersReducedMotion()) {
      finish();
      return;
    }

    this.#dialog.setAttribute('data-closing', '');
    const inner = this.#dialog.querySelector('.pf-dialog__inner');
    if (!inner) {
      finish();
      return;
    }
    let done = false;
    const onEnd = () => {
      if (done) return;
      done = true;
      inner.removeEventListener('animationend', onEnd);
      finish();
    };
    inner.addEventListener('animationend', onEnd);
    window.setTimeout(onEnd, 400); // safety net if the animation never fires
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  #lock() {
    if (this.#scrollLocked) return;
    this.#scrollLocked = true;
    this.#previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
  }

  #unlock() {
    if (!this.#scrollLocked) return;
    this.#scrollLocked = false;
    document.documentElement.style.overflow = this.#previousOverflow || '';
  }

  #onCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  #onBackdropClick = (event) => {
    // Native <dialog> reports clicks on the backdrop as clicks on the dialog itself.
    if (event.target === this.#dialog) this.close();
  };

  #onCloseClick = (event) => {
    if (event.target.closest('[data-pf-close]')) {
      event.preventDefault();
      this.close();
    }
  };

  /* ---- Swipe down to dismiss (mobile bottom sheet only) ---------------- */

  #isSheetLayout() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  #onTouchStart = (event) => {
    this.#drag = null;
    if (!this.isOpen || event.touches.length !== 1 || !this.#isSheetLayout()) return;
    if (this.#dialog.hasAttribute('data-closing')) return;

    const target = event.target;
    // Sliders and text fields own their own gestures.
    if (target.closest('input, select, textarea')) return;

    // Inside the scrolling body, only a pull from the very top drags the sheet —
    // otherwise the gesture is an ordinary scroll back up.
    const scroller = target.closest('.pf__body');
    if (scroller && scroller.scrollTop > 0) return;

    const touch = event.touches[0];
    this.#drag = {
      scroller,
      startX: touch.clientX,
      startY: touch.clientY,
      dy: 0,
      active: false,
      samples: [{ y: touch.clientY, t: event.timeStamp }],
    };
  };

  #onTouchMove = (event) => {
    const drag = this.#drag;
    if (!drag) return;
    const touch = event.touches[0];
    const dx = touch.clientX - drag.startX;
    const dy = touch.clientY - drag.startY;

    if (!drag.active) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      // Sideways (carousel) or upward (scroll) gestures are left alone.
      if (dy <= 0 || Math.abs(dx) > dy || (drag.scroller && drag.scroller.scrollTop > 0)) {
        this.#drag = null;
        return;
      }
      drag.active = true;
      drag.originY = touch.clientY; // start from here so the sheet doesn't jump 8px
      this.#dialog.setAttribute('data-swiped', '');
      this.#sheet.style.transition = 'none';
    }

    if (event.cancelable) event.preventDefault();
    drag.dy = Math.max(0, touch.clientY - drag.originY);
    drag.samples.push({ y: touch.clientY, t: event.timeStamp });
    if (drag.samples.length > 6) drag.samples.shift();

    this.#sheet.style.transform = `translate3d(0, ${drag.dy}px, 0)`;
    const height = this.#sheet.offsetHeight || window.innerHeight;
    this.#dialog.style.setProperty('--pf-sheet-drag', String(Math.min(1, drag.dy / height)));
  };

  #onTouchEnd = () => {
    const drag = this.#drag;
    this.#drag = null;
    if (!drag || !drag.active) return;

    const height = this.#sheet.offsetHeight || window.innerHeight;
    const last = drag.samples[drag.samples.length - 1];
    // Speed over the final ~120ms, so a slow drag that ends in a flick still counts.
    const first = drag.samples.find((s) => last.t - s.t <= 120) || drag.samples[0];
    const velocity = last.t > first.t ? (last.y - first.y) / (last.t - first.t) : 0; // px per ms

    const farEnough = drag.dy > Math.min(140, height * 0.25);
    const flicked = velocity > 0.45 && drag.dy > 30;

    if (farEnough || flicked) {
      this.#animateSheet(`translate3d(0, ${height}px, 0)`, '1', 220, () => {
        this.close({ immediate: true });
      });
    } else {
      this.#animateSheet('translate3d(0, 0, 0)', '0', 260, () => {
        this.#sheet.style.transform = '';
        this.#sheet.style.transition = '';
        this.#dialog.style.removeProperty('--pf-sheet-drag');
      });
    }
  };

  #animateSheet(transform, dragValue, duration, done) {
    const sheet = this.#sheet;
    let finished = false;
    const end = () => {
      if (finished) return;
      finished = true;
      sheet.removeEventListener('transitionend', end);
      done();
    };
    sheet.style.transition = `transform ${duration}ms cubic-bezier(0.32, 0.72, 0, 1)`;
    this.#dialog.style.setProperty('--pf-sheet-drag', dragValue);
    // Next frame, so the transition starts from the finger's position.
    requestAnimationFrame(() => {
      sheet.style.transform = transform;
      sheet.addEventListener('transitionend', end);
      window.setTimeout(end, duration + 120);
    });
  }
}

if (!customElements.get('paddle-fit-modal')) customElements.define('paddle-fit-modal', PaddleFitModal);

/* ------------------------------------------------------------------ *
 * Site-wide launcher — one delegated listener, bound once.
 * ------------------------------------------------------------------ */

/**
 * Plain links to the fitting page (menu items, rich-text links, buttons set up
 * in the theme editor) open the popup too, so merchants don't need to add
 * anything special to them. The href stays as the no-JS fallback.
 */
const FITTING_PATH = /^(\/[a-z]{2}(-[a-z]{2})?)?\/pages\/find-your-fit\/?$/i;
function fittingPageLink(target) {
  const link = target.closest('a[href]');
  if (!link || link.target === '_blank' || link.hasAttribute('download')) return null;
  // Header and footer navigation go to the full fitting page; the popup is for
  // calls to action within the page content.
  if (link.closest('#header-group, header, footer, header-drawer, header-menu')) return null;
  try {
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || !FITTING_PATH.test(url.pathname)) return null;
    return link;
  } catch (error) {
    return null;
  }
}

if (!window.__paddleFitLauncherBound) {
  window.__paddleFitLauncherBound = true;

  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest('[data-paddle-fit-open]') || fittingPageLink(event.target);
      if (!trigger) return;

      const modal = document.querySelector('paddle-fit-modal');
      // No modal on the page (or JS for it failed) — let the href navigate.
      if (!modal || typeof modal.open !== 'function') return;

      // Let modified clicks (new tab, etc.) behave normally.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

      event.preventDefault();
      // Launched from the mobile menu: tuck the menu away behind the fitting.
      const menu = trigger.closest('header-drawer');
      if (menu && typeof menu.close === 'function') {
        try { menu.close(); } catch (error) { /* ignore */ }
      }
      modal.open(trigger);
    },
    false
  );

  /**
   * Deep link: any storefront URL ending in #find-your-fit (or carrying
   * ?fit=open) opens the popup on arrival — for ads, emails, and previews.
   * The marker is removed from the address bar once used, so a reload or
   * the back button doesn't keep reopening it.
   */
  const wantsFit = () =>
    /^#(find-your-fit|paddle-fit)$/i.test(window.location.hash) ||
    new URLSearchParams(window.location.search).get('fit') === 'open';

  const openFromLink = () => {
    if (!wantsFit()) return;
    const modal = document.querySelector('paddle-fit-modal');
    if (!modal) return; // e.g. on the fitting page itself, which has no popup
    customElements.whenDefined('paddle-fit-modal').then(() => {
      if (typeof modal.open !== 'function' || modal.isOpen) return;
      modal.open();
      try {
        const url = new URL(window.location.href);
        url.hash = '';
        url.searchParams.delete('fit');
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (error) {
        /* leave the URL as it is */
      }
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', openFromLink, { once: true });
  else openFromLink();
  window.addEventListener('hashchange', openFromLink);
}
